import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useDedupedGet, seriesKey } from "@/lib/swr";
import { Link } from "react-router-dom";
import { Search, X, ChevronRight, Tag, SlidersHorizontal } from "lucide-react";
import AuthedImage from "@/components/AuthedImage";
import DiagnosticPanel from "@/components/DiagnosticPanel";
import HomepageGate from "@/components/HomepageGate";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { readCatalogCache, writeCatalogCache, paramsKey } from "@/lib/catalogCache";
import { usePriceVisibility, formatPriceForTrader } from "@/hooks/usePriceVisibility";

const formatINRStatic = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export default function CatalogPage() {
  const { user } = useAuth();
  const isTrader = !!user && (user.role === "admin" || user.status === "approved");
  // Presentation-mode toggle — when off, every price below renders as an em-dash.
  // Admins and unauthenticated visitors are unaffected (toggle only mounts in
  // the trader header). Public visitors see the static formatter regardless.
  const { visible: pricesVisible } = usePriceVisibility();
  const formatINR = (n) => (user && user.role !== "admin" ? formatPriceForTrader(n, pricesVisible) : formatINRStatic(n));
  // Seed series + bounds from the sessionStorage cache so back-navigation from
  // a series detail page renders the catalog INSTANTLY — without this every
  // tap-back triggered a fresh /api/series fetch over slow mobile (5–15 s on
  // installed PWA). A network fetch still runs below to revalidate, but the
  // user sees the cached list immediately and never stares at a spinner.
  const __cachedOnMount = useMemo(() => readCatalogCache(), []);
  const [series, setSeries] = useState(__cachedOnMount?.series || []);
  const [bounds, setBounds] = useState(__cachedOnMount?.bounds || { min: 0, max: 0 });
  const [loading, setLoading] = useState(!__cachedOnMount);
  // Track consecutive API failures. After 2 in a row we offer the user a
  // "Force refresh" button that clears the Service Worker + caches — the
  // common cause of "Failed to load catalog" on returning PWA users.
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const [range, setRange] = useState(() => {
    const b = __cachedOnMount?.bounds;
    return b ? [b.min, b.max] : [0, 0];
  });
  const [debouncedRange, setDebouncedRange] = useState(() => {
    const b = __cachedOnMount?.bounds;
    return b ? [b.min, b.max] : [0, 0];
  });
  const [availableOnly, setAvailableOnly] = useState(false);
  const [category, setCategory] = useState(""); // "" = All
  const [sortBy, setSortBy] = useState("price_asc"); // name_asc | price_asc | price_desc — default Low→High so price-shoppers see entry-level cards first
  // On mobile, only Search + religion chips are visible by default. Price slider,
  // "Available only" toggle and Sort live under "More filters" to keep more series
  // visible while scrolling. Desktop (lg:) ignores this flag and shows everything.
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  // ----- Server-side pagination state -----
  // Replaces the old "fetch up to 2000, slice client-side" approach. The
  // backend now ships 24 series per page; we append additional pages as the
  // user scrolls or hits "Load more". Cuts initial /api/series payload from
  // ~300 KB to ~10–15 KB and makes Cloudflare's edge cache useful.
  const PAGE_SIZE = 24;
  // Default to `true` so the user always gets a "Load more" affordance until
  // the first /api/series response tells us otherwise. (When the cache hit
  // gives us 24 stale tiles, there's likely page 2 on the server anyway.)
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(__cachedOnMount?.total || 0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Bumped each time a filter changes so a stale next-page fetch doesn't
  // race-write older results onto the new filter's list.
  const requestSeqRef = useRef(0);

  const CATEGORIES = ["Hindu", "Christian", "Common"];

  // Server is now the source of truth for sort order — slot-by-slot append
  // works across pages, and "Load more" never produces tiles in the wrong place.
  const sortedSeries = series;

  // Load price bounds once and init slider. Defensive .catch — if this call ever
  // fails (network blip, stale SW, transient 500), keep a sane default range so the
  // catalog page doesn't lock both slider ends to ₹0 (which would silently filter
  // out every series). If we already hydrated bounds from the sessionStorage cache,
  // this fetch quietly revalidates in the background without clobbering the user's
  // current slider position.
  useEffect(() => {
    api.get("/series/price-bounds")
      .then(({ data }) => {
        const lo = Math.floor(data.min || 0);
        const hi = Math.max(Math.ceil(data.max || 0), lo + 1);
        setBounds({ min: lo, max: hi });
        // Only reset the slider if it hasn't been hydrated yet (range still [0,0]).
        setRange((cur) => (cur[0] === 0 && cur[1] === 0 ? [lo, hi] : cur));
        setDebouncedRange((cur) => (cur[0] === 0 && cur[1] === 0 ? [lo, hi] : cur));
      })
      .catch(() => {
        // Sensible default — covers the whole catalog so the user still sees results.
        setBounds((cur) => (cur.min === 0 && cur.max === 0 ? { min: 0, max: 1000 } : cur));
        setRange((cur) => (cur[0] === 0 && cur[1] === 0 ? [0, 1000] : cur));
        setDebouncedRange((cur) => (cur[0] === 0 && cur[1] === 0 ? [0, 1000] : cur));
      });
  }, []);

  // Debounce slider changes
  useEffect(() => {
    const id = setTimeout(() => setDebouncedRange(range), 250);
    return () => clearTimeout(id);
  }, [range]);

  // Build the query params object for /api/series. Centralised so the initial
  // load and the "Load more" fetch always send identical filter values
  // (otherwise page 2's results wouldn't match page 1's filter).
  const buildParams = useCallback((page) => {
    const params = { lite: 1, sort: sortBy, page, page_size: PAGE_SIZE };
    if (q) params.q = q;
    if (debouncedRange[0] !== bounds.min) params.price_min = debouncedRange[0];
    if (debouncedRange[1] !== bounds.max) params.price_max = debouncedRange[1];
    if (availableOnly) params.available_only = true;
    if (category) params.category = category;
    return params;
  }, [q, debouncedRange, availableOnly, category, sortBy, bounds.min, bounds.max]);

  const load = useCallback(() => {
    // No-op: page-1 fetching is now owned by SWR via `useDedupedGet` below.
    // We expose this stable callback because other modules (back-navigation
    // refresh, error retry button, etc.) call `load()` to force a refresh —
    // they're routed through SWR's `mutate` so dedup + cache invalidation
    // happen the right way.
    refetchPage1Ref.current?.();
  }, []);

  // Page-1 fetcher — SWR handles dedup + a 30 s in-memory stale window so the
  // StrictMode-driven double-mount and the various effects that previously
  // each fired their own page-1 request now collapse into ONE network call.
  const page1Url = seriesKey(buildParams(1));
  const {
    data: page1Data,
    error: page1Error,
    isLoading: page1Loading,
    mutate: refetchPage1,
  } = useDedupedGet(page1Url);
  const refetchPage1Ref = useRef(refetchPage1);
  refetchPage1Ref.current = refetchPage1;

  // Fold SWR's response into the imperative state CatalogPage already owns
  // (series list, total count, has_more, error flag, persisted scroll cache).
  // Keeping the imperative state lets the "Load more" path append without
  // wrestling with SWR's infinite-loading API, which is a bigger refactor
  // than the user asked for.
  useEffect(() => {
    if (!page1Data) return;
    const items = Array.isArray(page1Data) ? page1Data : (page1Data.items || []);
    const newTotal = Array.isArray(page1Data) ? items.length : (page1Data.total || 0);
    const more = Array.isArray(page1Data) ? false : !!page1Data.has_more;
    setSeries(items);
    setTotal(newTotal);
    setHasMore(more);
    setLoadError(false);
    setLoading(false);
    // Persist for instant back-navigation. Keyed off the actual filter
    // params so on remount we can match the user's last view exactly.
    const params = buildParams(1);
    writeCatalogCache({
      series: items,
      bounds,
      total: newTotal,
      params: { ...params, _key: paramsKey(params) },
    });
  }, [page1Data, bounds, buildParams]);

  // Surface a network/server failure to the user without losing whatever
  // already-rendered tiles they have.
  useEffect(() => {
    if (page1Error) {
      setLoadError(true);
      setLoading(false);
      toast.error("Failed to load catalog");
    }
  }, [page1Error]);

  // Whenever the SWR key changes (i.e. filter / sort / search / price range),
  // the user is now looking at a new query — bump the request-seq counter so
  // any still-in-flight `loadMore()` for the OLD filter is dropped on arrival.
  useEffect(() => {
    requestSeqRef.current++;
    // Only show the global spinner when we have nothing to show; if SWR is
    // serving stale-but-acceptable data thanks to `keepPreviousData`, render
    // those tiles instead of blanking the grid.
    if (series.length === 0) setLoading(page1Loading);
  }, [page1Url, page1Loading, series.length]);

  // ----- Load more: fetches the next page and appends -----
  // Called by both the explicit "Load more" button and the idle-time auto-
  // prefetcher below. Guarded by `loadingMore` + the request-seq counter so
  // double-taps and stale responses can't duplicate or reorder tiles.
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    // Current page = ceil(series.length / PAGE_SIZE), so next page = +1.
    const nextPage = Math.floor(series.length / PAGE_SIZE) + 1;
    const params = buildParams(nextPage);
    const seq = requestSeqRef.current;
    api.get("/series", { params })
      .then(({ data }) => {
        if (seq !== requestSeqRef.current) return; // filter changed mid-flight
        const items = Array.isArray(data) ? [] : (data.items || []);
        const more = Array.isArray(data) ? false : !!data.has_more;
        setSeries((cur) => {
          // De-dupe defensively: if the same id is already present (shouldn't
          // happen with a stable server sort, but cheap insurance), keep the
          // first occurrence.
          const seen = new Set(cur.map((s) => s.id));
          const fresh = items.filter((s) => !seen.has(s.id));
          return [...cur, ...fresh];
        });
        setHasMore(more);
      })
      .catch(() => toast.error("Failed to load more"))
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasMore, series.length, buildParams]);

  // SWR's useDedupedGet handles the initial fetch automatically on mount and
  // refetches whenever the key (filter/sort/search/price params) changes. We
  // used to call `load()` here from a useEffect — but `load` is wired to
  // SWR's `mutate()`, which intentionally bypasses the deduping cache. Under
  // StrictMode that turned 1 dedup'd initial fetch into 3 (1 SWR auto-fetch +
  // 2 forced mutates from the double-effect). Keeping `load` available for
  // explicit user actions (retry button, back-nav refresh), but no longer
  // invoking it on mount.

  // Auto-retry once after 4 seconds when the initial load fails. The axios interceptor
  // already retries the request twice with backoff, so by the time we hit this we've
  // had 3 failed attempts in ~3s. One last try after a 4s wait covers brief Cloudflare
  // or backend hiccups that the user would otherwise have to recover from manually.
  useEffect(() => {
    if (!loadError) return undefined;
    const id = setTimeout(load, 4000);
    return () => clearTimeout(id);
  }, [loadError, load]);

  // Scroll restoration — when the user taps Classic 777, then taps back, we put them
  // RIGHT BACK ON Classic 777 (not where their saved pixel offset happens to land after
  // fonts/images/progressive-render reflow the page). The previous pixel-based restore
  // was correct in theory but kept landing on the wrong tile because web-font loading
  // and image decoding shifted every row by a few pixels — a 5,000-pixel saved offset
  // would then map to whatever row happened to be at that y on the freshly-rendered
  // page (Kiva 3000 instead of Classic 777, the bug the user kept reporting).
  //
  // New strategy:
  //   1. On tile click, save the clicked series ID to sessionStorage.
  //   2. On mount, after the first render with data, query that tile by data-testid
  //      and call scrollIntoView({ block: 'center' }) — bulletproof to layout shifts.
  //   3. Pixel offset is kept as a secondary fallback for the case where the user
  //      navigated away without clicking a tile (e.g. closed the tab and came back).
  useEffect(() => {
    let lastY = -1;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        if (Math.abs(y - lastY) > 8) {
          lastY = y;
          sessionStorage.setItem("sv_catalog_scroll", String(y));
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Restore the scroll position + "Last viewed" highlight on every mount of the
  // Catalog. Uses sessionStorage as the source of truth: every tile click stores
  // `sv_catalog_last_series` (the tile the user is about to leave for), and the
  // value PERSISTS across restores — so even if React StrictMode double-mounts
  // us (dev), a Service Worker hands us a cached HTML (prod), or the browser
  // back-forward cache replays a frozen snapshot, every restore opportunity
  // sees the SAME, latest, click-target id. The id is only ever replaced by a
  // newer click, never cleared by the restore code itself.
  const scrollRestoredRef = useRef(false);

  // Self-contained "Last viewed" highlight watcher. Runs on EVERY mount,
  // independent of catalog data-load state. Polls every 25 ms for the target
  // tile (read from sessionStorage on each tick so the latest click wins).
  // Once the tile appears in the DOM, starts a 3.2 s fade timer measured
  // FROM THAT MOMENT — so rapid back-navigation where the catalog is still
  // re-fetching data doesn't burn the user's highlight window before they
  // can even see it. Auto-cleans up on unmount.
  useEffect(() => {
    const HIGHLIGHT_CLASS = "sv-last-viewed";
    const targetId = sessionStorage.getItem("sv_catalog_last_series");
    if (!targetId) return undefined;
    const SELECTOR = `[data-testid="series-link-${targetId}"]`;
    let visibleAt = 0;       // ms timestamp when tile first appeared
    let highlightedEl = null; // remember so we can strip the class on fade
    const tick = () => {
      const cur = document.querySelector(SELECTOR);
      if (cur) {
        if (!visibleAt) visibleAt = Date.now();
        if (!cur.classList.contains(HIGHLIGHT_CLASS)) {
          cur.classList.add(HIGHLIGHT_CLASS);
        }
        highlightedEl = cur;
        // Fade out 3.2 s AFTER the tile first appeared (not 3.2 s after
        // mount — which would be the buggy old behaviour where the fade
        // could finish before the catalog even loaded).
        if (Date.now() - visibleAt > 3200) {
          window.clearInterval(pollId);
          if (highlightedEl) highlightedEl.classList.remove(HIGHLIGHT_CLASS);
        }
      }
    };
    const pollId = window.setInterval(tick, 25);
    // Hard ceiling so we never leak the interval even if something goes wrong
    // (e.g. tile never appears because user filtered the catalog).
    const hardStopId = window.setTimeout(() => window.clearInterval(pollId), 15000);
    return () => {
      window.clearInterval(pollId);
      window.clearTimeout(hardStopId);
      // Best-effort strip in case we're mid-fade — keeps DOM clean.
      if (highlightedEl) highlightedEl.classList.remove(HIGHLIGHT_CLASS);
    };
  }, []);

  useEffect(() => {
    // Always do the restore ourselves — never let the browser try to restore
    // scroll on its own (otherwise on bfcache it'll snap to a cached scroll
    // position and override ours).
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    // Retry-aware scroll restore. `attemptRestore` may run before React has
    // actually committed the tile DOM (the rAF chain isn't enough on slower
    // devices and in StrictMode-dev's double-mount cycle). If we don't find
    // the target tile yet, retry every 50 ms for up to 1 s before falling
    // back. Without this retry, the failing run would silently scroll to the
    // saved pixel offset, which after a few quick back-navigations puts the
    // user on the same wrong tile every time.
    const attemptRestore = (retriesLeft = 20) => {
      const lastSeriesId = sessionStorage.getItem("sv_catalog_last_series");
      const savedPixel = parseInt(sessionStorage.getItem("sv_catalog_scroll") || "0", 10);
      if (!lastSeriesId && savedPixel <= 0) {
        return;
      }
      let restored = false;
      if (lastSeriesId) {
        const el = document.querySelector(`[data-testid="series-link-${lastSeriesId}"]`);
        if (el) {
          // Brute-force the scroll position over ~1.2 s to defeat the browser's
          // own scrollRestoration races (and any debounced re-render that might
          // change layout afterwards). One scrollIntoView call is enough on a
          // quiet page, but rapid back-navigation triggers concurrent paint /
          // hydration churn that scrolls back to the previous trip's position.
          // Repeated calls win because nothing else is fighting us this hard.
          const TARGET_SELECTOR = `[data-testid="series-link-${lastSeriesId}"]`;
          const rescroll = () => {
            const cur = document.querySelector(TARGET_SELECTOR);
            if (cur) cur.scrollIntoView({ block: "center", behavior: "auto" });
          };
          rescroll();
          [50, 150, 300, 500, 800, 1200].forEach((d) => window.setTimeout(rescroll, d));
          restored = true;
        } else if (retriesLeft > 0) {
          // Tiles not in DOM yet — back off 50 ms and retry. Total budget ~1 s.
          window.setTimeout(() => attemptRestore(retriesLeft - 1), 50);
          return;
        }
      }
      // Fallback: if the saved tile is no longer in the list (admin deleted it
      // while user was on detail page), restore by pixel offset.
      if (!restored && savedPixel > 0) {
        window.scrollTo({ top: savedPixel, behavior: "auto" });
      }
    };

    // Schedule restore after fonts + 2 frames so row heights have settled.
    const scheduleRestore = () => {
      const fontsReady = (document.fonts && document.fonts.ready) || Promise.resolve();
      fontsReady.then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(attemptRestore);
        });
      });
    };

    // Run the restore on this mount once the catalog data is loaded. We only
    // run it ONCE per mount instance (guarded by scrollRestoredRef) so changing
    // the filter / sort later doesn't yank the user back to the old tile.
    if (!loading && sortedSeries.length > 0 && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true;
      scheduleRestore();
    }
  }, [loading, sortedSeries.length]);

  const empty = !loading && sortedSeries.length === 0;

  // ----- Idle-time auto-prefetch of the next page -----
  // The user expects the catalog to just keep filling as they scroll, not to
  // hit a hard "Load more" wall every 24 tiles. After page 1 paints, we kick
  // off the next page during browser idle time so by the time the user
  // scrolls into the second row, it's already rendered. Capped so we don't
  // dump the entire 500-series catalog into the DOM on every visit either —
  // the explicit "Load more" button (rendered below) covers anything beyond
  // 5 pages × PAGE_SIZE = 120 tiles.
  const AUTO_PREFETCH_PAGES = 5;
  const IDLE_INTERVAL_MS = 350;
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return undefined;
    const currentPage = Math.ceil(sortedSeries.length / PAGE_SIZE);
    if (currentPage >= AUTO_PREFETCH_PAGES) return undefined;
    const schedule = window.requestIdleCallback
      ? (cb) => window.requestIdleCallback(cb, { timeout: 800 })
      : (cb) => window.setTimeout(cb, IDLE_INTERVAL_MS);
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const handle = schedule(loadMore);
    return () => cancel(handle);
  }, [loading, loadingMore, hasMore, sortedSeries.length, loadMore]);

  const visibleSeries = sortedSeries;
  const stillLoadingMore = loadingMore;

  return (
    <div className="bg-[#FAF9F6]">
      {/* Public-visitor gate. Renders a full-screen overlay until the visitor
          either self-verifies via WhatsApp OTP or logs in as a registered
          buyer. Auto-hides for logged-in users (any role). Drop-in component
          — safe to remove if/when business rules change. */}
      <HomepageGate />
      {/* Retail visitor banner — drives traders to register for wholesale prices.
          Only shown when the visitor isn't yet a trader (admin or approved customer).
          Hidden on mobile (the small auth icon in the header serves the same purpose;
          this strip was eating ~40px of above-the-fold real estate on phones). */}
      {!isTrader && (
        <div
          className="hidden md:block bg-[#D62828] text-white text-center"
          data-testid="trader-signin-banner"
        >
          <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-16 py-2.5 flex flex-wrap items-center justify-center gap-2 text-xs">
            <Tag size={13} className="text-[#F4A100] shrink-0" />
            <span>
              <span className="text-[#F4A100] tracking-luxe">For traders & retailers:</span>{" "}
              Sign in to see <span className="font-semibold">wholesale prices</span>.
            </span>
            <Link
              to="/login"
              data-testid="banner-signin-link"
              className="underline underline-offset-2 tracking-luxe hover:text-[#F4A100]"
            >
              Sign in
            </Link>
            <span className="text-[#F4A100]/60">·</span>
            <Link
              to="/register"
              data-testid="banner-register-link"
              className="underline underline-offset-2 tracking-luxe hover:text-[#F4A100]"
            >
              Apply for trade access
            </Link>
            <span className="text-[#F4A100]/60">·</span>
            <Link
              to="/how-to-register"
              data-testid="banner-howto-link"
              className="underline underline-offset-2 tracking-luxe hover:text-[#F4A100]"
            >
              How to register
            </Link>
          </div>
        </div>
      )}

      {/* Sticky filter bar — compact on mobile (search + chips), expanded on lg+. The
          price slider, "Available only" toggle and Sort are collapsible on mobile via
          the "More" pill so the catalog rows appear above the fold while scrolling. */}
      {/* Sticky filter bar — uses `--app-header-offset` so it slides up to top:0 when
          the mobile header auto-hides on scroll-down, then slides back to top:44px when
          the user scrolls up. Desktop overrides via lg:!top-16 because the desktop
          header is taller and never auto-hides. */}
      <div
        className="sticky z-30 bg-[#FAF9F6]/95 backdrop-blur-md border-b border-[#E5E0D8] transition-[top] duration-300 ease-out lg:!top-16"
        style={{ top: "var(--app-header-offset, 44px)" }}
        data-testid="catalog-sticky-bar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-16 py-2 md:py-5 space-y-2 md:space-y-4">
          {/* Search + (mobile) More-filters toggle */}
          <div className="flex flex-col lg:flex-row gap-2.5 lg:gap-4 lg:items-center">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D62828]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, series code or theme"
                data-testid="catalog-search"
                className="w-full pl-10 pr-10 py-2.5 md:py-3.5 bg-white border border-[#E5E0D8] focus:border-[#F4A100] outline-none text-sm md:text-base text-[#1A1A1A]"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#4A4A4A] hover:text-[#D62828]"
                  data-testid="catalog-search-clear"
                  type="button"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Mobile-only: opens the collapsible filters drawer below. Shows a maroon dot
                + active-count badge so the user knows non-default filters are applied. */}
            <button
              onClick={() => setMoreFiltersOpen((v) => !v)}
              data-testid="filter-more-toggle"
              className={`lg:hidden inline-flex items-center justify-center gap-2 px-4 py-2.5 border transition-colors text-xs tracking-luxe ${
                moreFiltersOpen
                  ? "bg-[#D62828] border-[#D62828] text-white"
                  : "bg-white border-[#E5E0D8] text-[#4A4A4A] hover:border-[#F4A100]"
              }`}
            >
              <SlidersHorizontal size={14} />
              <span>More</span>
              {(availableOnly || sortBy !== "price_asc" || debouncedRange[0] !== bounds.min || debouncedRange[1] !== bounds.max) && (
                <span className={`w-1.5 h-1.5 rounded-full ${moreFiltersOpen ? "bg-[#F4A100]" : "bg-[#D62828]"}`} />
              )}
            </button>

            {/* Desktop-only: Available toggle inline with search */}
            <button
              onClick={() => setAvailableOnly((v) => !v)}
              data-testid="filter-available"
              className={`hidden lg:inline-flex items-center justify-center gap-2 px-5 py-3.5 border transition-colors whitespace-nowrap ${
                availableOnly
                  ? "bg-[#D62828] border-[#D62828] text-white"
                  : "bg-white border-[#E5E0D8] text-[#4A4A4A] hover:border-[#F4A100]"
              }`}
            >
              <span className="tracking-luxe text-xs">Available only</span>
            </button>
          </div>

          {/* Religion chips — always visible, horizontally scrollable on mobile so the
              row never wraps and steals vertical space from the series grid. */}
          <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-0.5 no-scrollbar" data-testid="category-chips">
            <button
              onClick={() => setCategory("")}
              data-testid="category-chip-all"
              className={`shrink-0 px-3 py-1.5 border tracking-luxe text-[10px] transition-colors rounded-full ${
                category === ""
                  ? "bg-[#D62828] border-[#D62828] text-white"
                  : "bg-white border-[#E5E0D8] text-[#4A4A4A] hover:border-[#F4A100]"
              }`}
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`category-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`shrink-0 px-3 py-1.5 border tracking-luxe text-[10px] transition-colors rounded-full ${
                  category === c
                    ? "bg-[#D62828] border-[#D62828] text-white"
                    : "bg-white border-[#E5E0D8] text-[#4A4A4A] hover:border-[#F4A100]"
                }`}
              >
                {c}
              </button>
            ))}
            {/* Desktop-only Sort selector lives at the right of the chip row */}
            <div className="flex-1 min-w-[8px] hidden lg:block" />
            <label className="hidden lg:flex items-center gap-2 ml-auto">
              <span className="tracking-luxe text-[10px] text-[#4A4A4A]">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-select"
                className="bg-white border border-[#E5E0D8] hover:border-[#F4A100] focus:border-[#F4A100] outline-none px-2.5 py-1.5 text-xs text-[#1A1A1A] tracking-luxe"
              >
                <option value="name_asc">A → Z (Name)</option>
                <option value="price_asc">Price · Low to High</option>
                <option value="price_desc">Price · High to Low</option>
              </select>
            </label>
          </div>

          {/* Mobile-only collapsible advanced filters: price slider + sort.
              Auto-closes after the user picks a value so the catalog reclaims
              the screen space immediately. */}
          {moreFiltersOpen && (
            <div className="lg:hidden space-y-3 pt-2 border-t border-[#E5E0D8]" data-testid="mobile-more-filters">
              {bounds.max > 0 && (
                <PriceRangeSlider
                  min={bounds.min}
                  max={bounds.max}
                  value={range}
                  onChange={(v) => { setRange(v); }}
                  onCommit={() => setMoreFiltersOpen(false)}
                />
              )}
              <label className="flex items-center justify-between gap-2">
                <span className="tracking-luxe text-[10px] text-[#4A4A4A]">Sort</span>
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setMoreFiltersOpen(false); }}
                  data-testid="sort-select-mobile"
                  className="bg-white border border-[#E5E0D8] focus:border-[#F4A100] outline-none px-2.5 py-1.5 text-xs text-[#1A1A1A] tracking-luxe"
                >
                  <option value="name_asc">A → Z (Name)</option>
                  <option value="price_asc">Price · Low to High</option>
                  <option value="price_desc">Price · High to Low</option>
                </select>
              </label>
            </div>
          )}

          {/* Desktop-only price slider (mobile shows it under "More") */}
          {bounds.max > 0 && (
            <div className="hidden lg:block">
              <PriceRangeSlider
                min={bounds.min}
                max={bounds.max}
                value={range}
                onChange={setRange}
              />
            </div>
          )}

          <div className="text-xs md:text-sm text-[#4A4A4A]" data-testid="catalog-count">
            {loading
              ? "Loading…"
              : `${sortedSeries.length} series${pricesVisible ? ` • ₹${debouncedRange[0]} – ₹${debouncedRange[1]}` : ""}`}
            {category && ` in ${category}`}
            {q && ` matching “${q}”`}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-16 py-10">
        {/* If the API failed (often a stale Service Worker on returning PWA users),
            offer a one-tap recovery: unregister the SW, clear caches, hard reload. */}
        {loadError && empty && (
          <div className="text-center py-16" data-testid="catalog-error">
            <div className="font-serif text-3xl text-[#D62828] mb-3">We hit a snag</div>
            <p className="text-[#4A4A4A] max-w-md mx-auto mb-6">
              The catalog couldn&apos;t load. This usually means your phone is holding an old cached
              copy of the app. Tap below to refresh it.
            </p>
            <button
              type="button"
              onClick={async () => {
                try {
                  // 1) Unregister every Service Worker on this origin
                  if ("serviceWorker" in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map((r) => r.unregister()));
                  }
                  // 2) Wipe every Cache Storage entry (app shell, runtime, etc.)
                  if ("caches" in window) {
                    const keys = await caches.keys();
                    await Promise.all(keys.map((k) => caches.delete(k)));
                  }
                  // 3) Drop any stale auth / wishlist state that might be tickling the API
                  try {
                    localStorage.removeItem("sv_token");
                    localStorage.removeItem("sv_user");
                  } catch { /* ignore */ }
                } catch { /* best effort */ }
                // 4) Hard-reload with a cache-busting query param so the browser can&apos;t
                //    serve any disk-cached HTML/JS. Adding `?fresh=ts` forces a clean fetch.
                const sep = window.location.href.includes("?") ? "&" : "?";
                window.location.href = window.location.href.split("?")[0] + sep + "fresh=" + Date.now();
              }}
              data-testid="catalog-force-refresh"
              className="inline-flex items-center gap-2 bg-[#D62828] hover:bg-[#8B1717] text-white px-6 py-3 tracking-luxe text-sm"
            >
              Force refresh (clear cache)
            </button>
            <details className="mt-6 max-w-md mx-auto text-left">
              <summary className="text-sm text-[#D62828] cursor-pointer underline" data-testid="catalog-diag-toggle">
                Still not working? Run diagnostics
              </summary>
              <DiagnosticPanel />
            </details>
            <p className="text-xs text-[#4A4A4A] mt-4">
              Or close all tabs and reopen{" "}
              <span className="font-medium">paperkraftcards.in</span>.
            </p>
          </div>
        )}

        {empty && !loadError && (
          <div className="text-center py-24" data-testid="catalog-empty">
            <div className="font-serif text-3xl text-[#D62828] mb-3">No series found</div>
            <p className="text-[#4A4A4A]">Try widening the price range or clearing the search.</p>
          </div>
        )}

        {loading && (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-5 bg-white border border-[#E5E0D8] p-4">
                <div className="w-24 h-28 sv-skeleton" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/2 sv-skeleton" />
                  <div className="h-3 w-1/3 sv-skeleton" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && sortedSeries.length > 0 && (
          <>
            <ul className="space-y-3">
              {visibleSeries.map((s, i) => (
                <SeriesRevealRow
                  key={s.id}
                  series={s}
                  index={i}
                  isTrader={isTrader}
                  category={category}
                  formatINR={formatINR}
                />
              ))}
            </ul>
            {stillLoadingMore && (
              <div
                data-testid="catalog-loading-more"
                className="flex items-center justify-center py-6 text-[#4A4A4A] text-xs tracking-luxe"
                aria-live="polite"
              >
                Loading more series…
              </div>
            )}
            {!stillLoadingMore && hasMore && (
              <div className="flex flex-col items-center gap-2 py-8" data-testid="catalog-load-more-wrap">
                <button
                  type="button"
                  onClick={loadMore}
                  data-testid="catalog-load-more-btn"
                  className="border border-[#D62828] text-[#D62828] hover:bg-[#D62828] hover:text-white px-8 py-3 tracking-luxe text-xs transition-colors"
                >
                  Load more
                </button>
                <div className="text-[10px] tracking-luxe text-[#4A4A4A]">
                  {sortedSeries.length.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")} shown
                </div>
              </div>
            )}
            {!stillLoadingMore && !hasMore && sortedSeries.length > PAGE_SIZE && (
              <div className="text-center py-8 text-[#F4A100] text-xs tracking-luxe" data-testid="catalog-end-of-list">
                · End of catalog · {sortedSeries.length} series shown ·
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PriceRangeSlider({ min, max, value, onChange, onCommit }) {
  const { visible: pricesVisible } = usePriceVisibility();
  const [lo, hi] = value;
  const setLo = (v) => onChange([Math.min(v, hi), hi]);
  const setHi = (v) => onChange([lo, Math.max(v, lo)]);
  const pct = (v) => ((v - min) / Math.max(1, max - min)) * 100;
  // Fires when the user releases the slider thumb — used by mobile More-drawer to
  // auto-close after the user picks a price range. No-op if `onCommit` isn't passed.
  const handleCommit = () => { if (onCommit) onCommit(); };

  return (
    <div className="flex flex-col gap-2" data-testid="price-range">
      <div className="flex items-center justify-between text-xs text-[#4A4A4A]">
        <span className="tracking-luxe text-[10px]">Price range</span>
        <span data-testid="price-range-label">
          {pricesVisible ? (
            <>
              <span className="text-[#D62828] font-medium">₹{lo.toFixed(2)}</span>
              <span className="mx-2 text-[#F4A100]">—</span>
              <span className="text-[#D62828] font-medium">₹{hi.toFixed(2)}</span>
            </>
          ) : (
            <span className="text-[#D62828] font-medium">—</span>
          )}
        </span>
      </div>
      <div className="relative h-8">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1.5 bg-[#E5E0D8] rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 bg-[#D62828] rounded-full"
          style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step="0.5"
          value={lo}
          onChange={(e) => setLo(parseFloat(e.target.value))}
          onMouseUp={handleCommit}
          onTouchEnd={handleCommit}
          onKeyUp={handleCommit}
          data-testid="price-min-slider"
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none sv-range"
        />
        <input
          type="range"
          min={min}
          max={max}
          step="0.5"
          value={hi}
          onChange={(e) => setHi(parseFloat(e.target.value))}
          onMouseUp={handleCommit}
          onTouchEnd={handleCommit}
          onKeyUp={handleCommit}
          data-testid="price-max-slider"
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none sv-range"
        />
      </div>
      <style>{`
        .sv-range::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          background: white;
          border: 2px solid #D62828;
          border-radius: 50%;
          pointer-events: auto;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
        }
        .sv-range::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: white;
          border: 2px solid #D62828;
          border-radius: 50%;
          pointer-events: auto;
          cursor: pointer;
        }

        /* "Last seen" indicator — soft golden glow + tiny pill that fades after 3s.
           Triggered by CatalogPage when the user returns from a series detail page.
           Uses position:relative so the absolutely-positioned pill anchors to the
           tile. The keyframe runs once and "forwards" keeps the final (faded)
           state until JS removes the class. */
        .sv-last-viewed {
          position: relative;
          animation: svLastSeenGlow 3000ms ease-out forwards;
          z-index: 1;
        }
        .sv-last-viewed::after {
          content: 'LAST VIEWED';
          position: absolute;
          top: -10px;
          right: 14px;
          background: #F4A100;
          color: #fff;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.14em;
          padding: 3px 9px;
          border-radius: 2px;
          pointer-events: none;
          box-shadow: 0 2px 8px rgba(197, 160, 89, 0.45);
          animation: svLastSeenPill 3000ms ease-out forwards;
        }
        @keyframes svLastSeenGlow {
          0% {
            box-shadow: 0 0 0 0 rgba(197, 160, 89, 0.55), 0 0 0 0 rgba(197, 160, 89, 0.35);
            border-color: #F4A100 !important;
          }
          15% {
            box-shadow: 0 0 0 3px rgba(197, 160, 89, 0.45), 0 0 22px 6px rgba(197, 160, 89, 0.30);
            border-color: #F4A100 !important;
          }
          80% {
            box-shadow: 0 0 0 2px rgba(197, 160, 89, 0.25), 0 0 14px 2px rgba(197, 160, 89, 0.12);
            border-color: rgba(197, 160, 89, 0.7) !important;
          }
          100% {
            box-shadow: 0 0 0 0 rgba(197, 160, 89, 0);
            border-color: inherit;
          }
        }
        @keyframes svLastSeenPill {
          0%   { opacity: 0; transform: translateY(-4px); }
          12%  { opacity: 1; transform: translateY(0); }
          75%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-2px); }
        }
        /* Respect users who prefer reduced motion — keep the colour cue but skip
           the glow/pulse animation. */
        @media (prefers-reduced-motion: reduce) {
          .sv-last-viewed { animation: none; border-color: #F4A100 !important; }
          .sv-last-viewed::after { animation: none; opacity: 1; }
        }
      `}</style>
    </div>
  );
}


// Individual series row with scroll-reveal entrance. Wrapping each row in its own
// component lets us call the `useScrollReveal` hook per-row while keeping the parent
// list lean. The transitionDelay creates a soft stagger as items enter the viewport.
function SeriesRevealRow({ series: s, index, isTrader, category, formatINR }) {
  const ref = useScrollReveal();
  // Stagger: first ~10 visible rows cascade in; beyond that we cap the delay so
  // late-scrolled items don't feel laggy.
  const delay = `${Math.min(index, 10) * 60}ms`;
  // Stash this series' id so that on back-navigation the catalog can scroll the
  // exact tile back into view — independent of pixel offsets, font-loading shifts
  // and image decoding (the previous pixel-based restore landed users on the
  // wrong row, e.g. Kiva 3000 instead of Classic 777).
  const markAsLastVisited = () => {
    try { sessionStorage.setItem("sv_catalog_last_series", String(s.id)); } catch (_e) { /* sessionStorage may be disabled in private mode — ignore */ }
  };
  return (
    <li ref={ref} style={{ transitionDelay: delay }}>
      <Link
        to={`/series/${s.id}${category ? `?religion=${encodeURIComponent(category)}` : ""}`}
        onClick={markAsLastVisited}
        data-testid={`series-link-${s.id}`}
        draggable={false}
        style={{ WebkitTapHighlightColor: "transparent", WebkitTouchCallout: "none" }}
        className="group flex items-stretch gap-3 sm:gap-5 md:gap-6 bg-white border border-[#E5E0D8] hover:border-[#F4A100] hover:shadow-lg hover:-translate-y-[2px] transition-all duration-300 p-2.5 sm:p-3 md:p-4 select-none touch-manipulation cursor-pointer active:bg-[#FAF6EE]"
      >
        {/* Thumbnail — fixed aspect on every viewport so the text column has predictable space */}
        <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-32 bg-[#F5F2EA] flex-shrink-0 sv-img-zoom relative pointer-events-none overflow-hidden">
          <AuthedImage
            path={s.sample_image_path}
            alt={s.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out"
            placeholderClassName="absolute inset-0 w-full h-full"
            thumb
            eager={index < 6}
            onContextMenu={(e) => e.preventDefault()}
            trackContext={{ seriesId: s.id }}
          />
        </div>

        {/* Text column. Vertical stack on phones so the name and price never collide
            (the old horizontal layout pushed the price into the title text on narrow
            widths like 360 px). On ≥ sm (≥ 640 px) we go back to a side-by-side layout
            because there's plenty of room. */}
        <div className="flex-1 min-w-0 pointer-events-none flex flex-col justify-center sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <h3
              className="font-serif text-[17px] sm:text-xl md:text-2xl text-[#1A1A1A] leading-tight select-none group-hover:text-[#D62828] transition-colors line-clamp-2 break-words pr-1"
              data-testid={`series-name-${s.id}`}
            >
              {s.name}
            </h3>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1.5">
              {s.code && <span className="tracking-luxe text-[10px] text-[#4A4A4A]">{s.code}</span>}
              {s.category && (
                <>
                  <span className="text-[#F4A100]">·</span>
                  <span className="text-[11px] sm:text-xs text-[#D62828]">{s.category}</span>
                </>
              )}
              <span className="text-[#F4A100]">·</span>
              <span className="text-[11px] sm:text-xs text-[#4A4A4A]" data-testid={`series-count-${s.id}`}>
                {s.card_count} {s.card_count === 1 ? "design" : "designs"}
              </span>
            </div>
          </div>

          {/* Price block — pinned to the right on ≥ sm; sits below the name on phones.
              `whitespace-nowrap` + `flex-shrink-0` guarantees it never wraps or
              overlaps the title. */}
          <div className="mt-2 sm:mt-0 sm:text-right flex-shrink-0 flex items-baseline sm:items-end sm:flex-col gap-1.5 sm:gap-0.5">
            <div
              className="font-sans text-lg sm:text-xl text-[#D62828] whitespace-nowrap leading-none"
              data-testid={`series-price-${s.id}`}
            >
              {formatINR(s.price)}
            </div>
            <div className="tracking-luxe text-[9px] sm:text-[10px] text-[#4A4A4A] whitespace-nowrap">
              per piece{!isTrader ? " · retail" : ""}
            </div>
          </div>
        </div>

        <ChevronRight size={18} className="self-center text-[#F4A100] group-hover:translate-x-1 transition-transform flex-shrink-0 hidden sm:block" />
      </Link>
    </li>
  );
}
